/**
 * FileUploadManager - Komponent do zarządzania plikami książek
 * Obsługuje upload plików PDF, DOC, DOCX do Supabase Storage
 * wojskaspecjalne.pl
 */

import React, { useState, useRef } from 'react';
import { Upload, X, File, FileText, CheckCircle, AlertCircle, Download, Trash2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { Button } from '../ui/button';
import './FileUploadManager.css';

interface BookFile {
  id: string;
  file_name: string;
  file_path: string;
  file_type: string;
  file_size: number | null;
  mime_type: string | null;
  is_primary: boolean;
  uploaded_at: string;
  download_count?: number;
  last_downloaded_at?: string | null;
}

interface FileUploadManagerProps {
  bookId?: string; // Jeśli jest book ID, to pliki mogą być od razu uploadowane
  onFilesChange?: (files: UploadedFileInfo[]) => void;
  existingFiles?: BookFile[];
  disabled?: boolean;
}

export interface UploadedFileInfo {
  id?: string; // Jeśli plik już istnieje w bazie
  file_name: string;
  file_path: string;
  file_type: string;
  file_size: number;
  mime_type: string;
  is_primary: boolean;
}

// Dozwolone typy plików i ich MIME types
const ALLOWED_FILE_TYPES = {
  'application/pdf': { ext: 'pdf', label: 'PDF', icon: FileText },
  'application/msword': { ext: 'doc', label: 'DOC', icon: FileText },
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': { ext: 'docx', label: 'DOCX', icon: FileText },
};

const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB

export const FileUploadManager: React.FC<FileUploadManagerProps> = ({
  bookId,
  onFilesChange,
  existingFiles = [],
  disabled = false,
}) => {
  const [files, setFiles] = useState<UploadedFileInfo[]>(
    existingFiles.map(f => ({
      id: f.id,
      file_name: f.file_name,
      file_path: f.file_path,
      file_type: f.file_type,
      file_size: f.file_size || 0,
      mime_type: f.mime_type || '',
      is_primary: f.is_primary,
    }))
  );
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});
  const [errors, setErrors] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  // Get file type label
  const getFileTypeInfo = (mimeType: string) => {
    return ALLOWED_FILE_TYPES[mimeType as keyof typeof ALLOWED_FILE_TYPES] || { 
      ext: 'file', 
      label: 'FILE', 
      icon: File 
    };
  };

  // Validate file
  const validateFile = (file: File): string | null => {
    // Check file type
    if (!Object.keys(ALLOWED_FILE_TYPES).includes(file.type)) {
      return `Niedozwolony typ pliku: ${file.type}. Dozwolone: PDF, DOC, DOCX`;
    }

    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      return `Plik zbyt duży: ${formatFileSize(file.size)}. Maksymalny rozmiar: ${formatFileSize(MAX_FILE_SIZE)}`;
    }

    return null;
  };

  // Upload file to Supabase Storage
  const uploadFileToStorage = async (file: File): Promise<string | null> => {
    try {
      // Generate unique filename
      const timestamp = Date.now();
      const randomStr = Math.random().toString(36).substring(2, 8);
      const ext = file.name.split('.').pop();
      const fileName = `${timestamp}_${randomStr}.${ext}`;
      const filePath = bookId ? `books/${bookId}/${fileName}` : `temp/${fileName}`;

      console.log('Uploading file to:', filePath);

      // Upload to Supabase Storage
      const { data, error } = await supabase.storage
        .from('library-files')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false,
        });

      if (error) {
        console.error('Storage upload error:', error);
        throw error;
      }

      console.log('File uploaded successfully:', data);

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('library-files')
        .getPublicUrl(filePath);

      return urlData.publicUrl;
    } catch (error) {
      console.error('Error uploading file:', error);
      return null;
    }
  };

  // Save file metadata to database (tylko jeśli mamy bookId)
  const saveFileToDatabase = async (fileInfo: UploadedFileInfo): Promise<string | null> => {
    if (!bookId) return null;

    try {
      const { data, error } = await supabase
        .from('book_files')
        .insert({
          book_id: bookId,
          file_name: fileInfo.file_name,
          file_path: fileInfo.file_path,
          file_type: fileInfo.file_type,
          file_size: fileInfo.file_size,
          mime_type: fileInfo.mime_type,
          is_primary: fileInfo.is_primary,
        })
        .select()
        .single();

      if (error) throw error;

      return data.id;
    } catch (error) {
      console.error('Error saving file to database:', error);
      return null;
    }
  };

  // Handle file selection
  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files || []);
    if (selectedFiles.length === 0) return;

    setUploading(true);
    setErrors([]);

    const newFiles: UploadedFileInfo[] = [];
    const newErrors: string[] = [];

    for (const file of selectedFiles) {
      // Validate
      const validationError = validateFile(file);
      if (validationError) {
        newErrors.push(`${file.name}: ${validationError}`);
        continue;
      }

      // Upload to storage
      setUploadProgress(prev => ({ ...prev, [file.name]: 0 }));
      
      const filePath = await uploadFileToStorage(file);
      
      if (!filePath) {
        newErrors.push(`${file.name}: Błąd podczas uploadu`);
        continue;
      }

      setUploadProgress(prev => ({ ...prev, [file.name]: 100 }));

      const fileInfo: UploadedFileInfo = {
        file_name: file.name,
        file_path: filePath,
        file_type: getFileTypeInfo(file.type).ext,
        file_size: file.size,
        mime_type: file.type,
        is_primary: files.length === 0 && newFiles.length === 0, // Pierwszy plik jest primary
      };

      // Jeśli mamy bookId, zapisz od razu w bazie
      if (bookId) {
        const fileId = await saveFileToDatabase(fileInfo);
        if (fileId) {
          fileInfo.id = fileId;
        }
      }

      newFiles.push(fileInfo);
    }

    // Update state
    const updatedFiles = [...files, ...newFiles];
    setFiles(updatedFiles);
    setErrors(newErrors);
    setUploading(false);
    setUploadProgress({});

    // Notify parent
    onFilesChange?.(updatedFiles);

    // Clear input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Remove file
  const handleRemoveFile = async (index: number) => {
    const file = files[index];

    // Jeśli plik ma ID, usuń z bazy
    if (file.id && bookId) {
      try {
        const { error } = await supabase
          .from('book_files')
          .delete()
          .eq('id', file.id);

        if (error) throw error;

        // Usuń z storage
        const pathParts = file.file_path.split('/');
        const storagePath = pathParts.slice(pathParts.indexOf('library-files') + 1).join('/');
        
        await supabase.storage
          .from('library-files')
          .remove([storagePath]);
      } catch (error) {
        console.error('Error deleting file:', error);
        setErrors([`Błąd podczas usuwania pliku: ${file.file_name}`]);
        return;
      }
    }

    const updatedFiles = files.filter((_, i) => i !== index);
    setFiles(updatedFiles);
    onFilesChange?.(updatedFiles);
  };

  // Toggle primary file
  const handleTogglePrimary = (index: number) => {
    const updatedFiles = files.map((f, i) => ({
      ...f,
      is_primary: i === index,
    }));
    setFiles(updatedFiles);
    onFilesChange?.(updatedFiles);
  };

  return (
    <div className="file-upload-manager">
      <div className="file-upload-header">
        <h3>Pliki książki</h3>
        <p className="file-upload-description">
          Dodaj pliki PDF, DOC lub DOCX (max. {formatFileSize(MAX_FILE_SIZE)})
        </p>
      </div>

      {/* Upload Button */}
      <div className="file-upload-zone">
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          multiple
          onChange={handleFileSelect}
          disabled={disabled || uploading}
          style={{ display: 'none' }}
        />
        <Button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled || uploading}
          className="upload-button"
        >
          <Upload size={20} aria-hidden="true" />
          {uploading ? 'Uploadowanie...' : 'Wybierz pliki'}
        </Button>
        {uploading && (
          <div className="upload-status">
            <span className="upload-spinner" aria-hidden="true">⏳</span>
            Uploadowanie plików...
          </div>
        )}
      </div>

      {/* Errors */}
      {errors.length > 0 && (
        <div className="file-upload-errors">
          {errors.map((error, i) => (
            <div key={i} className="error-item">
              <AlertCircle size={16} aria-hidden="true" />
              <span>{error}</span>
            </div>
          ))}
        </div>
      )}

      {/* Files List */}
      {files.length > 0 && (
        <div className="files-list">
          {files.map((file, index) => {
            const typeInfo = getFileTypeInfo(file.mime_type);
            const IconComponent = typeInfo.icon;
            
            return (
              <div key={index} className="file-item">
                <div className="file-item-icon">
                  <IconComponent size={24} aria-hidden="true" />
                </div>
                
                <div className="file-item-info">
                  <div className="file-item-name">
                    {file.file_name}
                    {file.is_primary && (
                      <span className="primary-badge" title="Plik główny">
                        <CheckCircle size={14} aria-hidden="true" /> Główny
                      </span>
                    )}
                  </div>
                  <div className="file-item-meta">
                    <span className="file-type-badge">{typeInfo.label}</span>
                    <span className="file-size">{formatFileSize(file.file_size)}</span>
                  </div>
                </div>

                <div className="file-item-actions">
                  {!file.is_primary && (
                    <Button
                      type="button"
                      onClick={() => handleTogglePrimary(index)}
                      disabled={disabled}
                      className="btn-set-primary"
                      title="Ustaw jako plik główny"
                    >
                      Ustaw jako główny
                    </Button>
                  )}
                  
                  {file.file_path && (
                    <Button
                      type="button"
                      onClick={() => window.open(file.file_path, '_blank')}
                      className="btn-download"
                      title="Pobierz plik"
                    >
                      <Download size={18} aria-hidden="true" />
                    </Button>
                  )}
                  
                  <Button
                    type="button"
                    onClick={() => handleRemoveFile(index)}
                    disabled={disabled}
                    className="btn-remove"
                    title="Usuń plik"
                  >
                    <Trash2 size={18} aria-hidden="true" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {files.length === 0 && !uploading && (
        <div className="no-files-message">
          <File size={48} strokeWidth={1} aria-hidden="true" />
          <p>Brak plików. Kliknij "Wybierz pliki", aby dodać pliki PDF, DOC lub DOCX.</p>
        </div>
      )}
    </div>
  );
};

